import { showConfirmation } from '../lib/confirmation';
import Select from '../components/ui/Select'
import DateInput from '../components/ui/DateInput'
import Input from '../components/ui/Input'
import Textarea from '../components/ui/Textarea'
import Checkbox from '../components/ui/Checkbox'
import MyDropzoneMulti from '../components/MyDropzoneMulti'
import { Table, TableHead, TableRow, TableHeaderCell, TableCell, TableEmpty } from '../components/ui/Table'
import ActionMenu from '../components/ui/ActionMenu'
import Button from '../components/ui/Button'
import SectionCard from '../components/ui/SectionCard'
import { useCallback, useEffect, useMemo, useState } from 'react'
import './Quotes.css'
import { useSelector } from 'react-redux'
import { addDays } from 'date-fns'
import { formatBusinessDate as format } from '../utils/businessTime'
import { toast } from 'react-toastify'
import Header from '../components/Header'
import Sidebar from '../components/Sidebar'
import LoadingPopup from '../components/LoadingPopup'
import ModalComponent from '../components/Modal'
import { DownloadIcon, EditIcon, EyeIcon, FileTextIcon, MailIcon, TrashIcon, CheckIcon } from '../components/NavIcons'
import { postWithAuth } from '../common'
import { workerTypes } from '../constants'
import { downloadQuotePdf, previewQuotePdf } from '../components/QuoteGenerator'

const quoteServiceOptions = [...new Set([
    ...workerTypes,
    'Team Leader',
    'Traffic Management Plan',
    'Equipment Hire',
    'Travel / Mobilisation',
])]
const quoteDurationUnits = ['Hours', 'Days', 'Weeks', 'Unit']

const emptyItem = () => ({
    id: `quote-item-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    description: 'Traffic Controller',
    quantity: '1',
    duration: '8',
    durationUnit: 'Hours',
    rate: '',
    gstEnabled: true,
})

const emptyForm = () => ({
    clientId: '',
    siteId: '',
    location: '',
    quoteDate: format(new Date(), 'yyyy-MM-dd'),
    validUntil: format(addDays(new Date(), 30), 'yyyy-MM-dd'),
    reference: '',
    notes: '',
    attachments: [],
    items: [emptyItem()],
})

const money = value => `$${Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
const splitEmails = value => String(value || '').split(/[;,]/).map(email => email.trim()).filter(Boolean)
const validEmail = value => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim())

const calculateTotals = items => {
    const normalized = items.map(item => {
        const subtotal = Number(item.quantity || 0) * Number(item.duration || 0) * Number(item.rate || 0)
        const gst = item.gstEnabled === false ? 0 : subtotal * 0.1
        return { ...item, subtotal, gst, total: subtotal + gst }
    })
    return {
        items: normalized,
        subtotal: normalized.reduce((sum, item) => sum + item.subtotal, 0),
        gst: normalized.reduce((sum, item) => sum + item.gst, 0),
        total: normalized.reduce((sum, item) => sum + item.total, 0),
    }
}

const hydrateQuote = quote => ({
    clientId: quote.client?.id || '',
    siteId: quote.site?.id || '',
    location: quote.location || '',
    quoteDate: String(quote.quoteDate || '').slice(0, 10),
    validUntil: String(quote.validUntil || '').slice(0, 10),
    reference: quote.reference || '',
    notes: quote.notes || '',
    attachments: quote.attachments || [],
    items: (quote.items || []).map(item => ({ ...item, quantity: String(item.quantity), duration: String(item.duration), durationUnit: item.durationUnit || 'Hours', rate: String(item.rate) })),
})

const statusClass = status => `quoteStatus quoteStatus${status || 'Draft'}`

const Quotes = ({ embedded = false }) => {
    const activeOrg = useSelector(state => state.user.activeOrg)
    const activeOrgInfo = useSelector(state => state.user.activeOrgInfo) || {}
    const isAdmin = activeOrg?.role === 'admin'
    const logo = typeof activeOrgInfo.logo === 'string' ? activeOrgInfo.logo : activeOrgInfo.logo?.url
    const company = {
        name: activeOrgInfo.name || '',
        address: activeOrgInfo.address || '',
        phone: activeOrgInfo.phone || '',
        email: activeOrgInfo.primaryEmail || '',
        abn: activeOrgInfo.abn || '',
    }

    const [quotes, setQuotes] = useState([])
    const [clients, setClients] = useState([])
    const [sites, setSites] = useState([])
    const [loading, setLoading] = useState(false)
    const [uploading, setUploading] = useState(false)
    const [attachmentFolder, setAttachmentFolder] = useState(() => `new-${Date.now()}-${Math.random().toString(36).slice(2)}`)
    const [editorOpen, setEditorOpen] = useState(false)
    const [editingQuote, setEditingQuote] = useState(null)
    const [form, setForm] = useState(emptyForm)
    const [search, setSearch] = useState('')
    const [statusFilter, setStatusFilter] = useState('All')
    const [emailQuote, setEmailQuote] = useState(null)
    const [emailTo, setEmailTo] = useState('')
    const [emailCc, setEmailCc] = useState('')
    const [emailSubject, setEmailSubject] = useState('')
    const [emailBody, setEmailBody] = useState('')
    const [statusReason, setStatusReason] = useState('')
    const [lineItemEditor, setLineItemEditor] = useState(null)
    const [deleteOpen, setDeleteOpen] = useState(false)
    const [deleting, setDeleting] = useState(false)
    const [acceptingQuote, setAcceptingQuote] = useState(null)
    const [accepting, setAccepting] = useState(false)
    const [markSentQuote, setMarkSentQuote] = useState(null)
    const [markingSent, setMarkingSent] = useState(false)
    const markQuoteSent = async () => {
        if (!markSentQuote || markingSent) return
        setMarkingSent(true)
        try {
            const response = await postWithAuth('updateQuoteStatus', { orgId: activeOrg.id, id: markSentQuote.id, status: 'Sent', reason: 'Manually marked as sent. No email was sent.' })
            if (response?.err) throw new Error(response.err)
            if (response?.data?.status !== 'Sent') throw new Error('Unable to confirm quote status.')
            setEditingQuote(current => current?.id === markSentQuote.id ? response.data : current)
            setMarkSentQuote(null)
            showConfirmation('Quote marked as sent. No email was sent.')
            await refresh()
        } catch (error) { toast.error(error.message || 'Unable to mark quote as sent.') }
        finally { setMarkingSent(false) }
    }
    const acceptAndCreateInvoice = async () => {
        if (!acceptingQuote || accepting) return
        setAccepting(true)
        let accepted = acceptingQuote.status === 'Accepted'
        try {
            if (!accepted) {
                const response = await postWithAuth('updateQuoteStatus', { orgId: activeOrg.id, id: acceptingQuote.id, status: 'Accepted' })
                if (response?.err) throw new Error(response.err)
                if (response?.data?.status !== 'Accepted') throw new Error('Unable to confirm quote acceptance.')
                accepted = true
                setAcceptingQuote(response.data)
                setEditingQuote(current => current?.id === acceptingQuote.id ? response.data : current)
            }
            const response = await postWithAuth('convertQuoteToInvoice', { orgId: activeOrg.id, id: acceptingQuote.id })
            if (response?.err) throw new Error(response.err)
            if (!response?.data?.id) throw new Error('Unable to confirm invoice creation.')
            const convertedInvoice = { id: response.data.id, invoiceNumber: response.data.invoiceNumber }
            setEditingQuote(current => current?.id === acceptingQuote.id ? { ...current, status: 'Accepted', convertedInvoice } : current)
            setAcceptingQuote(null)
            showConfirmation('Quote accepted. Draft invoice ' + response.data.invoiceNumber + (response.data.alreadyConverted ? ' already exists.' : ' created.'))
            await refresh()
        } catch (error) {
            toast.error(accepted ? 'Quote is accepted, but invoice creation failed: ' + error.message + '. Retry to create the invoice.' : error.message)
        } finally { setAccepting(false) }
    }

    const deleteQuote = async () => {
        if (!editingQuote || deleting) return
        setDeleting(true)
        try {
            const response = await postWithAuth('deleteQuote', { orgId: activeOrg.id, id: editingQuote.id })
            if (response?.err) throw new Error(response.err)
            if (response?.data?.id !== editingQuote.id) throw new Error('Unable to confirm quote deletion.')
            setQuotes(current => current.filter(quote => quote.id !== editingQuote.id))
            setDeleteOpen(false)
            setEditorOpen(false)
            setEditingQuote(null)
            showConfirmation('Quote deleted.')
        } catch (error) {
            toast.error(error.message || 'Unable to delete quote.')
        } finally {
            setDeleting(false)
        }
    }

    const refresh = useCallback(async () => {
        if (!activeOrg?.id || !isAdmin) return
        setLoading(true)
        try {
            const [quoteResponse, clientResponse, siteResponse] = await Promise.all([
                postWithAuth('fetchQuotes', { orgId: activeOrg.id }),
                postWithAuth('fetchClients', { orgId: activeOrg.id, listOnly: true }),
                postWithAuth('fetchSites', { orgId: activeOrg.id, listOnly: true }),
            ])
            if (quoteResponse?.err) throw new Error(quoteResponse.err)
            if (clientResponse?.err) throw new Error(clientResponse.err)
            if (siteResponse?.err) throw new Error(siteResponse.err)
            setQuotes(Array.isArray(quoteResponse?.data) ? quoteResponse.data : [])
            setClients(Array.isArray(clientResponse?.data) ? clientResponse.data : [])
            setSites(Array.isArray(siteResponse?.data) ? siteResponse.data : [])
        } catch (error) {
            toast.error(error.message || 'Unable to load quotes.')
        } finally {
            setLoading(false)
        }
    }, [activeOrg?.id, isAdmin])

    useEffect(() => { refresh() }, [refresh])

    const availableSites = useMemo(
        () => sites.filter(site => !site.archived && site.clientId === form.clientId),
        [sites, form.clientId]
    )
    const formTotals = useMemo(() => calculateTotals(form.items), [form.items])
    const isDirty = useMemo(() => {
        if (!editingQuote) return false
        return JSON.stringify(form) !== JSON.stringify(hydrateQuote(editingQuote))
    }, [editingQuote, form])
    const filteredQuotes = useMemo(() => {
        const needle = search.trim().toLowerCase()
        return quotes.filter(quote => {
            if (statusFilter !== 'All' && quote.status !== statusFilter) return false
            if (!needle) return true
            return [quote.quoteNumber, quote.client?.name, quote.site?.name, quote.location, quote.reference]
                .some(value => String(value || '').toLowerCase().includes(needle))
        })
    }, [quotes, search, statusFilter])

    const openCreate = () => {
        setAttachmentFolder(`new-${Date.now()}-${Math.random().toString(36).slice(2)}`)
        setEditingQuote(null)
        setForm(emptyForm())
        setEditorOpen(true)
    }

    const openQuote = quote => {
        setEditingQuote(quote)
        setForm(hydrateQuote(quote))
        setStatusReason('')
        setEditorOpen(true)
    }

    const openLineItemEditor = (item = null, index = form.items.length) => setLineItemEditor({
        index,
        description: item?.description || '',
        rate: item?.rate === undefined ? '' : String(item.rate),
        quantity: item ? String(Number(item.quantity || 0) * Number(item.duration || 1)) : '',
        quantityType: item?.durationUnit || 'Hours',
        gstEnabled: item?.gstEnabled !== false,
    })

    const saveLineItem = () => {
        const description = String(lineItemEditor?.description || '').trim()
        const rate = Number(lineItemEditor?.rate)
        const quantity = Number(lineItemEditor?.quantity)
        if (!description) return toast.error('Description is required.')
        if (!Number.isFinite(rate) || rate < 0 || !Number.isFinite(quantity) || quantity <= 0) return toast.error('Rate and quantity must be valid positive values.')
        const nextItem = {
            id: form.items[lineItemEditor.index]?.id || `quote-item-${Date.now()}-${Math.random().toString(36).slice(2)}`,
            description,
            rate: String(rate),
            quantity: String(quantity),
            duration: '1',
            durationUnit: lineItemEditor.quantityType,
            gstEnabled: lineItemEditor.gstEnabled,
        }
        setForm(current => ({
            ...current,
            items: lineItemEditor.index < current.items.length
                ? current.items.map((item, index) => index === lineItemEditor.index ? nextItem : item)
                : [...current.items, nextItem],
        }))
        setLineItemEditor(null)
    }

    const validateForm = () => {
        if (!form.clientId) return 'Select a client.'
        if (!form.quoteDate || !form.validUntil) return 'Enter the quote and valid-until dates.'
        if (form.validUntil < form.quoteDate) return 'Valid-until date cannot be before the quote date.'
        if (!form.items.length) return 'Add at least one quote item.'
        const invalidRow = form.items.find(item => !item.description.trim() || !(Number(item.quantity) > 0) || !(Number(item.duration) > 0) || !(Number(item.rate) >= 0))
        return invalidRow ? 'Every quote item requires a description, quantity, duration and rate.' : ''
    }

    const saveQuote = async ({ close = true } = {}) => {
        if (uploading) return toast.error('Wait for attachments to finish uploading.')
        const error = validateForm()
        if (error) {
            toast.error(error)
            return null
        }
        setLoading(true)
        try {
            const endpoint = editingQuote ? 'updateQuote' : 'createQuote'
            const response = await postWithAuth(endpoint, {
                orgId: activeOrg.id,
                ...(editingQuote ? { id: editingQuote.id } : {}),
                ...form,
            })
            if (response?.err) throw new Error(response.err)
            if (JSON.stringify((response?.data?.attachments || []).map(file => file.filePath || file.path || file.url)) !== JSON.stringify(form.attachments.map(file => file.filePath || file.path || file.url))) {
                throw new Error('The server has not saved the quote attachments. Keep this window open and retry after the attachment update is deployed.')
            }
            showConfirmation(editingQuote ? 'Quote updated.' : `Quote ${response.data.quoteNumber} created.`)
            setEditingQuote(response.data)
            setForm(hydrateQuote(response.data))
            if (close) setEditorOpen(false)
            await refresh()
            return response.data
        } catch (errorValue) {
            toast.error(errorValue.message || 'Unable to save quote.')
            return null
        } finally {
            setLoading(false)
        }
    }

    const quoteForPdf = quote => ({ ...quote, company })
    const currentFormQuote = () => ({
        ...(editingQuote || {}),
        client: clients.find(client => client.id === form.clientId) || editingQuote?.client || null,
        site: sites.find(site => site.id === form.siteId) || null,
        ...form,
        ...formTotals,
    })

    const runPdfAction = async (action, quote) => {
        try {
            await action(quoteForPdf(quote), logo)
        } catch (error) {
            toast.error(error.message || 'Unable to generate the quote PDF.')
        }
    }

    const changeStatus = async status => {
        if (!editingQuote) return
        if (isDirty) return toast.error('Save the quote changes before updating its status.')
        if (status === 'Voided' && !window.confirm(`Void Quote ${editingQuote.quoteNumber}? This cannot be undone.`)) return
        setLoading(true)
        try {
            const response = await postWithAuth('updateQuoteStatus', {
                orgId: activeOrg.id,
                id: editingQuote.id,
                status,
                reason: statusReason,
            })
            if (response?.err) throw new Error(response.err)
            showConfirmation(`Quote marked ${status.toLowerCase()}.`)
            setEditingQuote(response.data)
            setForm(hydrateQuote(response.data))
            setStatusReason('')
            await refresh()
        } catch (error) {
            toast.error(error.message || 'Unable to update quote status.')
        } finally {
            setLoading(false)
        }
    }

    const openEmail = quote => {
        const currentClient = clients.find(client => client.id === quote.client?.id)
        setEmailQuote(quote)
        setEmailTo(quote.client?.email || currentClient?.invoiceEmail || currentClient?.primaryEmail || '')
        setEmailCc(quote.client?.invoiceCcEmails || currentClient?.invoiceCcEmails || '')
        setEmailSubject(`Quote ${quote.quoteNumber} - ${company.name}`)
        setEmailBody(`Hi ${quote.client?.name || 'there'},\n\nPlease find attached Quote ${quote.quoteNumber}.\n\nPlease let us know if you have any questions.\n\nKind regards,\n${company.name}`)
    }

    const saveAndOpenEmail = async () => {
        const quote = isDirty ? await saveQuote({ close: false }) : editingQuote
        if (quote) openEmail(quote)
    }

    const sendQuote = async () => {
        if (!validEmail(emailTo)) return toast.error('Enter a valid recipient email.')
        const cc = splitEmails(emailCc)
        if (cc.some(email => !validEmail(email))) return toast.error('One or more CC email addresses are invalid.')
        if (!emailSubject.trim() || !emailBody.trim()) return toast.error('Subject and email body are required.')
        setLoading(true)
        try {
            const response = await postWithAuth('sendQuote', {
                orgId: activeOrg.id,
                id: emailQuote.id,
                to: emailTo.trim(),
                cc,
                subject: emailSubject.trim(),
                emailBody: emailBody.trim(),
            })
            if (response?.err) throw new Error(response.err)
            showConfirmation('Quote sent.')
            setEmailQuote(null)
            setEditorOpen(false)
            await refresh()
        } catch (error) {
            toast.error(error.message || 'Unable to send quote.')
        } finally {
            setLoading(false)
        }
    }

    const convertToInvoice = async () => {
        if (!editingQuote?.id || editingQuote.status !== 'Accepted') return
        setLoading(true)
        try {
            const response = await postWithAuth('convertQuoteToInvoice', { orgId: activeOrg.id, id: editingQuote.id })
            if (response?.err) throw new Error(response.err)
            showConfirmation(response.data?.alreadyConverted ? `Invoice ${response.data.invoiceNumber} already exists.` : `Draft invoice ${response.data.invoiceNumber} created.`)
            await refresh()
            setEditingQuote(current => ({ ...current, convertedInvoice: { id: response.data.id, invoiceNumber: response.data.invoiceNumber } }))
        } catch (error) {
            toast.error(error.message || 'Unable to convert quote to invoice.')
        } finally {
            setLoading(false)
        }
    }

    if (!isAdmin) return <div><Header /><div className='pageShell'><Sidebar /><main className='pageContent pageContentPadded'><div className='pageTitleBlock'><h3 className='pageTitle'>Quotes</h3><p className='pageSubtitle'>Quotes are available to administrators.</p></div></main></div></div>

    const editable = !editingQuote || !['Accepted', 'Voided'].includes(editingQuote.status)

    const ContentTag = embedded ? 'div' : 'main'
    return <div>
        {!embedded && <Header />}
        <div className={embedded ? undefined : 'pageShell'}>
            {!embedded && <Sidebar />}
            <LoadingPopup visible={loading} message='Processing quote...' />
            <ContentTag className={embedded ? 'quotePage quotePageEmbedded' : 'pageContent pageContentPadded quotePage'}>
                <SectionCard title='Create Quote' subtitle='Prepare, send and track client quotes.' className='sectionCard width-100 invoiceManualGenerationCard' bodyClassName='sectionBody sectionCardBody'>
                    <div className='invoiceManualGenerationBlock'>
                        <div className='invoiceManualGenerationAction invoiceGenerationActions'>
                            <Button variant="create" size="md" type='button' className='invoiceInlineSaveButton saveWithIcon' onClick={openCreate}>Create Quote</Button>
                        </div>
                    </div>
                </SectionCard>

                <section className='sectionCard quoteFilters'>
                    <div className='sectionBody sectionCardBody'>
                        <input aria-label='Search quotes' placeholder='Search quote, client, site or reference' value={search} onChange={event => setSearch(event.target.value)} />
                        <select aria-label='Filter quote status' value={statusFilter} onChange={event => setStatusFilter(event.target.value)}>
                            {['All', 'Draft', 'Sent', 'Accepted', 'Declined', 'Voided'].map(status => <option key={status}>{status}</option>)}
                        </select>
                    </div>
                </section>

                <section className='sectionCard'>
                    <div className='sectionCardHeader'><h3 className='sectionCardTitle'>Quote History</h3></div>
                    <div className='sectionBody sectionCardBody quoteTableWrap'>
                        <Table className='invoiceListTable quoteListTable'>
                            <TableHead><TableRow><TableHeaderCell kind="number">Quote</TableHeaderCell><TableHeaderCell>Client</TableHeaderCell><TableHeaderCell>Site / Location</TableHeaderCell><TableHeaderCell>Date</TableHeaderCell><TableHeaderCell>Valid Until</TableHeaderCell><TableHeaderCell>Status</TableHeaderCell><TableHeaderCell>Total</TableHeaderCell><TableHeaderCell>Action</TableHeaderCell></TableRow></TableHead>
                            <tbody>
                                {filteredQuotes.map(quote => <TableRow key={quote.id}>
                                    <TableCell kind="number"><button type='button' className='quoteNumberLink' onClick={() => openQuote(quote)}>{quote.quoteNumber}</button></TableCell>
                                    <TableCell>{quote.client?.name || '-'}</TableCell>
                                    <TableCell>{quote.location || quote.site?.name || '-'}</TableCell>
                                    <TableCell>{quote.quoteDate ? format(new Date(quote.quoteDate), 'd MMM yyyy') : '-'}</TableCell>
                                    <TableCell>{quote.validUntil ? format(new Date(quote.validUntil), 'd MMM yyyy') : '-'}</TableCell>
                                    <TableCell>{quote.status || 'Draft'}</TableCell>
                                    <TableCell className="invoiceTotalCell">{money(quote.total)}</TableCell>
                                    <TableCell><ActionMenu label="More quote actions" className='quoteRowActions'>
                                        <Button variant="secondary" size="icon" aria-label="View quote" className='recordActionIcon' title='View quote' onClick={() => openQuote(quote)}><EyeIcon /></Button>
                                        {!['Accepted', 'Voided'].includes(quote.status) && <Button variant="secondary" size="icon" data-action-tone="edit" aria-label="Edit quote" className='recordActionIcon' title='Edit quote' onClick={() => openQuote(quote)}><EditIcon /></Button>}
                                        <Button variant="secondary" size="icon" aria-label="Preview PDF" className='recordActionIcon' title='Preview PDF' onClick={() => runPdfAction(previewQuotePdf, quote)}><FileTextIcon /></Button>
                                        {(quote.status === 'Draft') && <Button variant='secondary' size='icon' className='recordActionIcon' title='Mark as sent' aria-label='Mark as sent' onClick={() => setMarkSentQuote(quote)}><MailIcon /></Button>}
                                        {(['Draft', 'Sent'].includes(quote.status) || (quote.status === 'Accepted' && !quote.convertedInvoice?.id)) && <Button variant='secondary' size='icon' className='recordActionIcon' title={quote.status === 'Accepted' ? 'Create invoice' : 'Mark as accepted'} aria-label={quote.status === 'Accepted' ? 'Create invoice' : 'Mark as accepted'} onClick={() => setAcceptingQuote(quote)}><CheckIcon /></Button>}
                                        <Button variant="secondary" size="icon" aria-label="Download PDF" className='recordActionIcon' title='Download PDF' onClick={() => runPdfAction(downloadQuotePdf, quote)}><DownloadIcon /></Button>
                                        {!['Accepted', 'Voided'].includes(quote.status) && <Button variant="secondary" size="icon" aria-label="Email quote" className='recordActionIcon' title='Email quote' onClick={() => openEmail(quote)}><MailIcon /></Button>}
                                    </ActionMenu></TableCell>
                                </TableRow>)}
                                {!filteredQuotes.length && <TableEmpty colSpan={8}>No quotes found.</TableEmpty>}
                            </tbody>
                        </Table>
                    </div>
                </section>
            </ContentTag>
        </div>

        <ModalComponent showModal={editorOpen} setShowModal={setEditorOpen} customMaxWidth='1000px' customContentStyle={{ maxHeight: '85vh', overflowY: 'auto' }}>
            <div className='quoteEditor'>
                <div className='invoiceEditHeaderBanner quoteEditorHeader'>
                    <div><span className={statusClass(editingQuote?.status || 'Draft')}>{editingQuote?.status || 'Draft'}</span><h2 className='invoiceEditSectionHeading invoiceDetailsTitle'>Quote Details</h2></div>
                    <div className='invoiceEditHeaderActions quoteEditorHeaderActions'>
                        {editingQuote && <Button variant="secondary" size="md" type='button'  onClick={() => runPdfAction(previewQuotePdf, currentFormQuote())}>Preview PDF</Button>}
                        <Button variant="secondary" size="md" type='button'  onClick={() => setEditorOpen(false)}>Close</Button>
                        {editable && <Button variant="soft" size="md" data-action-tone="save" type='button'  disabled={uploading || loading} onClick={saveQuote}>Save Quote</Button>}
                    </div>
                </div>

                <div className='invoiceEditMetaRow quoteMetaGrid'>
<label>Quote Number<Input disabled value={editingQuote?.quoteNumber || 'New Quote'} /></label>
                    <div className="quoteSelectField"><label htmlFor="quote-client">Client <span className="quoteRequired">*</span></label><Select id="quote-client" disabled={!editable || !!editingQuote} value={form.clientId} onChange={event => setForm(current => ({ ...current, clientId: event.target.value, siteId: '' }))}><option value=''>Select client</option>{clients.map(client => <option value={client.id} key={client.id}>{client.name}</option>)}</Select></div>
                    <div className='quoteSelectField'><label htmlFor='quote-site'>Site (optional)</label><Select id='quote-site' disabled={!editable} value={form.siteId} onChange={event => setForm(current => ({ ...current, siteId: event.target.value }))}><option value=''>No specific site</option>{availableSites.map(site => <option value={site.id} key={site.id}>{site.name}</option>)}</Select></div>
                    <label>Site location (optional)<Input disabled={!editable} value={form.location} onChange={event => setForm(current => ({ ...current, location: event.target.value }))} placeholder='Enter address or location' /></label>
                    <label>Reference<Input disabled={!editable} value={form.reference} onChange={event => setForm(current => ({ ...current, reference: event.target.value }))} placeholder='Optional client reference' /></label>
                    <label><span>Quote date <span className="quoteRequired">*</span></span><DateInput disabled={!editable} value={form.quoteDate} onChange={event => setForm(current => ({ ...current, quoteDate: event.target.value }))} /></label>
                    <label><span>Valid until <span className="quoteRequired">*</span></span><DateInput disabled={!editable} value={form.validUntil} onChange={event => setForm(current => ({ ...current, validUntil: event.target.value }))} /></label>
                </div>

                <div className='quoteLineItems'>

                    <Table className='invoiceLineItemsTable quoteServicesTable'>
                        <TableHead><TableRow><TableHeaderCell kind="text">Description</TableHeaderCell><TableHeaderCell>Quantity</TableHeaderCell><TableHeaderCell>Type</TableHeaderCell><TableHeaderCell>Rate</TableHeaderCell><TableHeaderCell>GST</TableHeaderCell><TableHeaderCell>Total</TableHeaderCell>{editable && <TableHeaderCell>Action</TableHeaderCell>}</TableRow></TableHead>
                        <tbody>{formTotals.items.map((item, index) => <TableRow key={item.id}>
                            <TableCell kind="text" className="quoteItemDescription">{item.description}</TableCell><TableCell>{Number(item.quantity || 0) * Number(item.duration || 0)}</TableCell><TableCell>{item.durationUnit || 'Hours'}</TableCell><TableCell>{money(item.rate)}</TableCell><TableCell>{money(item.gst)}</TableCell>
                            <TableCell className='invoiceTotalCell'>{money(item.total)}</TableCell>
                            {editable && <TableCell><div className="invoiceLineItemActions"><Button variant="secondary" size="icon" className="recordActionIcon invoiceLineEditIcon" data-action-tone="edit" type='button' title="Edit line item" aria-label="Edit" onClick={() => openLineItemEditor(item, index)}><EditIcon /></Button><Button variant="danger" size="icon" className="recordActionIcon invoiceLineDeleteIcon" data-action-tone="delete" type='button' title="Remove line item" aria-label="Remove" disabled={form.items.length === 1} onClick={() => setForm(current => ({ ...current, items: current.items.filter((_, itemIndex) => itemIndex !== index) }))}><TrashIcon /></Button></div></TableCell>}
                        </TableRow>)}
<TableRow className='invoiceNewLineItemRow'><TableCell colSpan={editable ? 7 : 6}>
<div className='invoiceNewLineItemGrid'>
<div className='invoiceLineItemAddActions'>{editable && <Button variant='create' size='md' className='invoiceInlineLink' onClick={() => openLineItemEditor()}>+ New Line Item</Button>}</div>
<div className='invoicePaymentTotals'><strong>Subtotal</strong><span>{money(formTotals.subtotal)}</span><strong>GST</strong><span>{money(formTotals.gst)}</span><strong>Total</strong><strong>{money(formTotals.total)}</strong></div>
</div></TableCell></TableRow></tbody>
                    </Table>
                </div>

                <div className='quoteFooterGrid'>
                    <label>Notes<Textarea disabled={!editable} value={form.notes} onChange={event => setForm(current => ({ ...current, notes: event.target.value }))} placeholder='Scope, exclusions, assumptions or other quote notes' /></label>

                </div>

                <section className='invoiceEditDividerSection'>
                    <h2 className='invoiceEditSectionHeading'>Attachments</h2>
                    <MyDropzoneMulti disabled={!editable || loading} editing={editable} storageLocation={`QuoteAttachments/${editingQuote?.id || attachmentFolder}`} errors={[]} name='quoteAttachments' images={form.attachments} updateImages={attachments => setForm(current => ({ ...current, attachments }))} setUploading={setUploading} allowPdf />
                    {uploading && <p role='status'>Uploading attachments…</p>}
                </section>

                {editingQuote && <div className='quoteLifecycle'>
                    <div className='quoteSectionHeading'><h3 className='invoiceEditSectionHeading'>Quote Status</h3>{!['Accepted', 'Voided'].includes(editingQuote.status) && <Button variant="soft" size="md" data-action-tone="save" type='button'  onClick={saveAndOpenEmail}><MailIcon /> Email Quote</Button>}</div>
                    {editingQuote.status === 'Accepted' && <div className='quoteConversionRow'><div><strong>{editingQuote.convertedInvoice?.invoiceNumber ? `Invoice ${editingQuote.convertedInvoice.invoiceNumber} created` : 'Create a standalone draft invoice from this accepted quote'}</strong><p>This invoice is not linked to workers, allocations or timesheets.</p></div><Button variant="soft" size="md" data-action-tone="save" type='button'  disabled={!!editingQuote.convertedInvoice?.invoiceNumber} onClick={convertToInvoice}>{editingQuote.convertedInvoice?.invoiceNumber ? 'Converted' : 'Convert to Invoice'}</Button></div>}
                    <Input value={statusReason} onChange={event => setStatusReason(event.target.value)} placeholder='Optional status note' />
                    <div className='quoteStatusActions'>
                        {editingQuote.status === 'Draft' && <><Button variant='secondary' disabled={isDirty || uploading} onClick={() => setMarkSentQuote(editingQuote)}>Mark as sent</Button><Button variant='soft' disabled={isDirty || uploading} onClick={() => setAcceptingQuote(editingQuote)}>Mark as accepted</Button></>}
                        {editingQuote.status === 'Sent' && <><Button variant="soft" size="md" data-action-tone="save"  disabled={isDirty || uploading} onClick={() => setAcceptingQuote(editingQuote)}>Mark as accepted</Button><Button variant="secondary" size="md"  onClick={() => changeStatus('Declined')}>Mark Declined</Button></>}
                        {!['Voided'].includes(editingQuote.status) && <Button variant="danger" size="md"  onClick={() => changeStatus('Voided')}>Void Quote</Button>}
                    </div>
                </div>}

                {editingQuote && <section className='quoteLifecycle' aria-label='Delete quote'>
                    <h3 className='invoiceEditSectionHeading'>Delete Quote</h3>
                    <p>Permanently remove this quote. Any invoice created from it will be kept.</p>
                    <Button variant='danger' disabled={loading || deleting} onClick={() => setDeleteOpen(true)}>Delete Quote</Button>
                </section>}
                {editingQuote?.history?.length > 0 && <div className='quoteHistory'><h3 className='invoiceEditSectionHeading'>History</h3>{[...editingQuote.history].reverse().map((entry, index) => <div key={`${entry.date}-${index}`}><strong>{entry.action}</strong><span>{entry.date ? format(new Date(entry.date), 'd MMM yyyy, h:mm a') : ''} · {entry.actorName || 'System'}</span>{entry.details && <p>{entry.details}</p>}</div>)}</div>}
            </div>
        </ModalComponent>

        <ModalComponent showModal={!!markSentQuote} setShowModal={() => !markingSent && setMarkSentQuote(null)} customOverlayStyle={{ zIndex: 3200 }}>
            <div className='quoteEmailModal'><h2>Mark as sent</h2><p>Mark {markSentQuote?.quoteNumber} as sent without emailing it?</p><div className='modalFooter'><Button variant='secondary' disabled={markingSent} onClick={() => setMarkSentQuote(null)}>Cancel</Button><Button variant='soft' disabled={markingSent} onClick={markQuoteSent}>{markingSent ? 'Updating?' : 'Confirm mark as sent'}</Button></div></div>
        </ModalComponent>
        <ModalComponent showModal={!!acceptingQuote} setShowModal={() => !accepting && setAcceptingQuote(null)} customOverlayStyle={{ zIndex: 3200 }}>
            <div className='quoteEmailModal'>
                <h2>Mark as accepted</h2>
                <p>Accept {acceptingQuote?.quoteNumber} and create a draft client invoice? The quote will be kept for your records.</p>
                <div className='modalFooter'><Button variant='secondary' disabled={accepting} onClick={() => setAcceptingQuote(null)}>Cancel</Button><Button variant='soft' disabled={accepting} onClick={acceptAndCreateInvoice}>{accepting ? 'Creating invoice?' : 'Accept and create invoice'}</Button></div>
            </div>
        </ModalComponent>
        <ModalComponent showModal={deleteOpen} setShowModal={() => !deleting && setDeleteOpen(false)} customOverlayStyle={{ zIndex: 3200 }} customMaxWidth='480px'>
            <div className='quoteEmailModal'>
                <h2>Delete {editingQuote?.quoteNumber}?</h2>
                <p>This permanently removes the quote and its history. This cannot be undone. Any invoice created from this quote will be kept.</p>
                <div className='modalFooter'>
                    <Button variant='secondary' disabled={deleting} onClick={() => setDeleteOpen(false)}>Cancel</Button>
                    <Button variant='danger' disabled={deleting} onClick={deleteQuote}>{deleting ? 'Deleting…' : 'Confirm Delete'}</Button>
                </div>
            </div>
        </ModalComponent>

        <ModalComponent showModal={!!lineItemEditor} setShowModal={setLineItemEditor} customOverlayStyle={{ zIndex: 3100 }}>
            <div className='invoiceModalStack quoteLineItemModal'>
                <h2 className='modalTitle'>Line Item</h2>
                <label>Description<Textarea aria-label='Line item description' value={lineItemEditor?.description || ''} onChange={event => setLineItemEditor(current => ({ ...current, description: event.target.value }))} placeholder='Description' /></label>
                <label>Rate<div className='quoteMoneyInput'><span>$</span><Input aria-label='Line item rate' type='number' min='0' step='0.01' value={lineItemEditor?.rate || ''} onChange={event => setLineItemEditor(current => ({ ...current, rate: event.target.value }))} placeholder='0.00' /></div></label>
                <label>Quantity Type<select aria-label='Line item quantity type' value={lineItemEditor?.quantityType || 'Hours'} onChange={event => setLineItemEditor(current => ({ ...current, quantityType: event.target.value }))}>{quoteDurationUnits.map(unit => <option key={unit}>{unit}</option>)}</select></label>
                <label>Quantity<Input aria-label='Line item quantity' type='number' min='0.01' step='0.25' value={lineItemEditor?.quantity || ''} onChange={event => setLineItemEditor(current => ({ ...current, quantity: event.target.value }))} placeholder='Quantity' /></label>
                <div className='invoiceLineItemSummaryRow'><label className='invoiceLineGstToggle'><Checkbox checked={lineItemEditor?.gstEnabled !== false} onChange={event => setLineItemEditor(current => ({ ...current, gstEnabled: event.target.checked }))} />Apply GST (10%)</label><div className='invoiceLineItemAmounts'><p><b>GST:</b> {money((Number(lineItemEditor?.rate || 0) * Number(lineItemEditor?.quantity || 0)) * (lineItemEditor?.gstEnabled === false ? 0 : 0.1))}</p><p><b>Total:</b> {money((Number(lineItemEditor?.rate || 0) * Number(lineItemEditor?.quantity || 0)) * (lineItemEditor?.gstEnabled === false ? 1 : 1.1))}</p></div></div>
                <div className='modalFooter'><Button variant="secondary" size="md"  onClick={() => setLineItemEditor(null)}>Cancel</Button><Button variant="soft" size="md" data-action-tone="save"  onClick={saveLineItem}>Save</Button></div>
            </div>
        </ModalComponent>

        <ModalComponent showModal={!!emailQuote} setShowModal={() => setEmailQuote(null)} customMaxWidth='720px'>
            <div className='quoteEmailModal'><h2>Review Email</h2><p>The quote PDF will be attached.</p>
                <label>To <input value={emailTo} onChange={event => setEmailTo(event.target.value)} /></label>
                <label>CC (optional) <input value={emailCc} onChange={event => setEmailCc(event.target.value)} placeholder='Separate multiple emails with commas' /></label>
                <label>Subject <input value={emailSubject} onChange={event => setEmailSubject(event.target.value)} /></label>
                <label>Email Body <textarea value={emailBody} onChange={event => setEmailBody(event.target.value)} /></label>
                <div className='quoteEmailAttachment'><FileTextIcon /><span>{emailQuote?.quoteNumber}.pdf</span></div>
                <div className='modalFooter'><Button variant="secondary" size="md" type='button'  onClick={() => setEmailQuote(null)}>Cancel</Button><Button variant="soft" size="md" data-action-tone="save" type='button'  onClick={sendQuote}>Send Quote</Button></div>
            </div>
        </ModalComponent>
    </div>
}

export default Quotes
